<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useSlots, useTemplateRef, watch } from 'vue';
import ChevronIcon from '@/assets/icons/shared/chevron.svg';
import SearchIcon from '@/assets/icons/shared/search.svg';
import CloseIcon from '@/assets/icons/shared/close.svg';
import LockIcon from '@/assets/icons/core/lock.svg';
import PlusIcon from '@/assets/icons/shared/plus.svg';
import { isEqual } from 'lodash-es';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import type { OptionsFilter, OptionsGetter, SelectEvent } from '@/modules/common/types/ui.ts';
import UiDropdownList from '@/modules/ui/components/ui/UiDropdownList.vue';
import UiDropdown from '@/modules/ui/components/ui/UiDropdown.vue';
import UiDropdownDivider from '@/modules/ui/components/ui/UiDropdownDivider.vue';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { StringUtil } from '@/modules/common/utils/string.util.ts';
import { useI18n } from 'vue-i18n';
import { useMaxLength } from '@/modules/ui/composables/use-max-length.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import UiButtonIcon from '@/modules/ui/components/ui/UiButtonIcon.vue';
import UiChipList from '@/modules/ui/components/ui/UiChipList.vue';
import UiSpinner from '@/modules/ui/components/ui/UiSpinner.vue';

const SEARCH_MIN_OPTIONS = 7;
const MULTI_SELECT_KEYS = ['Control', 'Meta', 'Shift'];

const model = defineModel<null | Option | Option[]>({ default: null });

const {
  color = 'gray',
  size = 'm',
  options = [],
  filter = (options) => options,
  placeholder = 'ui.combobox.selectValue',
  multiple,
  noClear,
  noChevron,
  withCustomOption,
  withSearch,
  updateAfterLoad,
  chooseAfterLoad,
  clearOnOpen,
  expanded,
  label,
  disabled,
  readonly,
  error,
  searchMaxLength,
  pinSelected,
  highlightMatches,
  isOptionEditable,
  editOption,
  addOption,
  addOptionLabel,
  typeToAddLabel = 'ui.combobox.typeToAdd',
  singleLine = false,
  searchByCode,
  inlineSearch = false,
} = defineProps<{
  label?: string;
  error?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  color?: 'gray' | 'dark-gray' | 'white';
  size?: 'm' | 'l';
  options?: Option[] | OptionsGetter<string | number>;
  filter?: OptionsFilter<string | number>;
  placeholder?: string;
  multiple?: boolean;
  noClear?: boolean;
  noChevron?: boolean;
  withCustomOption?: boolean;
  withSearch?: boolean;
  updateAfterLoad?: boolean;
  chooseAfterLoad?: number | string;
  clearOnOpen?: boolean;
  expanded?: boolean;
  searchMaxLength?: number;
  focusOnMount?: boolean;
  pinSelected?: boolean;
  highlightMatches?: boolean;
  isOptionEditable?: (option: Option) => boolean;
  editOption?: (option: Option) => void;
  addOption?: () => void;
  addOptionLabel?: string;
  typeToAddLabel?: string;
  singleLine?: boolean;
  searchByCode?: boolean;
  inlineSearch?: boolean;
}>();

const emit = defineEmits<{
  focus: [];
  blur: [];
  load: [options: Option[]];
}>();

const slots = useSlots();

const { signal } = useAbort();

const { maybeT } = useI18n();

const listRef = useTemplateRef('list');
const dropdownRef = useTemplateRef('dropdown');
const fieldRef = useTemplateRef('field');
const searchInputRef = useTemplateRef<HTMLInputElement>('searchInput');

const input = ref('');
const asyncOptions = ref<Option[]>([]);
const isLoading = ref(false);
const multiSelectKeyPressed = ref(false);
const isFocused = ref(false);
const isOpen = ref(false);

const offset = computed(() => (size === 'l' ? 8 : 4));

const comboboxOptions = computed(() => (typeof options === 'function' ? asyncOptions.value : options));

const filteredOptions = computed(() => filter(comboboxOptions.value));

const canAddOption = computed(() => !!addOption && !!addOptionLabel);

const innerDisabled = computed(
  () => disabled || (!isLoading.value && !filteredOptions.value.length && !withCustomOption && !canAddOption.value)
);

const className = computed(() => {
  return [
    {
      _prefix: !!slots.prefix,
      _postfix: !!slots.postfix,
      _disabled: innerDisabled.value,
      _readonly: !!readonly,
      _error: error,
    },
    `_size-${size}`,
    `_color-${color}`,
  ];
});

const hasOptions = computed(() => (multiple ? !!(model.value as Option[]).length : !!model.value));

const showSearch = computed(() => withCustomOption || withSearch || filteredOptions.value.length > SEARCH_MIN_OPTIONS);

const showInlineSearch = computed(() => showSearch.value && inlineSearch && !multiple && !singleLine);

const showDropdownSearch = computed(() => showSearch.value && !showInlineSearch.value);

const isInlineSearchOpen = computed(() => showInlineSearch.value && isOpen.value);

const listQuery = computed(() => StringUtil.normalize(input.value.trim()));

const foundOptions = computed(() => {
  const query = listQuery.value;
  const matches = filteredOptions.value.filter((option) => {
    if (!query) {
      return true;
    }

    if (StringUtil.normalize(maybeT(option.label)).includes(query)) {
      return true;
    }

    return !!searchByCode && StringUtil.normalize(String(option.code)).includes(query);
  });

  if (!pinSelected || !query || multiple || !model.value || Array.isArray(model.value)) {
    return matches;
  }

  const selected = model.value;
  const selectedOption = filteredOptions.value.find((option) => option.code === selected.code);

  if (!selectedOption) {
    return matches;
  }

  return [selectedOption, ...matches.filter((option) => option.code !== selected.code)];
});

const customOption = computed(() => {
  const label = input.value.trim();

  if (!withCustomOption || !label || foundOptions.value.some((option) => option.label === label)) {
    return null;
  }

  return {
    label,
    code: label,
  };
});

const extendedOptions = computed(() => foundOptions.value);

const showAddOption = computed(() => canAddOption.value);

const showCustomOptionPlate = computed(() => !!withCustomOption && !showAddOption.value);

const showEmptyState = computed(
  () => !extendedOptions.value.length && !showAddOption.value && !showCustomOptionPlate.value
);

const showClear = computed(() => !noClear && hasOptions.value && !readonly && !disabled);

const showLock = computed(() => !!readonly);

const showChevron = computed(() => !noChevron);

const isValueExpanded = computed(() => expanded && !singleLine);

const { gradient: searchGradient, onInput: onSearchInput } = useMaxLength(
  input,
  computed(() => searchMaxLength),
  ref(false)
);

const searchGradientStyle = computed(() => ({
  background: `linear-gradient(
    to right,
    rgba(226, 231, 239, 1) 0%,
    rgba(226, 231, 239, 1) ${searchGradient.value.start},
    transparent ${searchGradient.value.end},
    transparent 100%
  )`,
}));

watch(input, () => listRef.value?.setActive(null));

watch(innerDisabled, (disabled) => {
  if (disabled) {
    dropdownRef.value?.hide();
  }
});

function getOptionsByCodes(codes: string | number | (string | number)[]) {
  return multiple
    ? filteredOptions.value.filter((option) => (codes as (string | number)[]).some((code) => option.code === code))
    : filteredOptions.value.find((option) => option.code === (codes as string | number));
}

function getOptionsByIndexes(indexes: number | number[]) {
  return multiple
    ? filteredOptions.value.filter((_, index) => (indexes as number[]).includes(index))
    : filteredOptions.value[indexes as number];
}

function choose(codesOrIndexes: string | number | (string | number)[], byIndexes = false) {
  const value = byIndexes
    ? getOptionsByIndexes(codesOrIndexes as number | number[])
    : getOptionsByCodes(codesOrIndexes);

  if (!value || (Array.isArray(value) && !value.length)) {
    return;
  }

  if (!isEqual(model.value, value)) {
    model.value = value;
  }
}

async function getOptions(isReloading = false) {
  isLoading.value = true;

  try {
    asyncOptions.value = await (options as OptionsGetter<string | number>)(signal.value);
    if (chooseAfterLoad) {
      choose(chooseAfterLoad);
    }
    if (isReloading || updateAfterLoad) {
      updateModel();
    }
    emit('load', asyncOptions.value);
  } catch (error) {
    if (!ErrorUtil.isUnexpected(error)) {
      return;
    }

    notification.error(
      { key: 'ui.notification.error.field.getOptions', params: { field: maybeT(label || placeholder) } },
      error
    );
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  if (typeof options === 'function') {
    getOptions();
  }
});

function updateModel() {
  if (multiple) {
    const array = model.value as Option[];
    if (array.length) {
      choose(array.map(({ code }) => code));
    }
  } else if (model.value) {
    choose((model.value as Option).code);
  }
}

watch(
  () => options,
  (options) => {
    if (typeof options === 'function') {
      getOptions(true);
    } else {
      updateModel();
    }
  }
);

function select(option: Option) {
  model.value = option;
}

function add(option: Option) {
  model.value = [...(model.value as Option[]), option];
}

function remove(option: Option) {
  model.value = (model.value as Option[]).filter((checkedOption) => checkedOption.code !== option.code);
}

function onSelect({ option, checked }: SelectEvent) {
  if (!multiple) {
    if (!checked) {
      select(option);
    }

    dropdownRef.value?.hide();
    nextTick(restoreFieldFocus);

    return;
  }

  if (checked) {
    remove(option);
  } else {
    add(option);
  }

  if (!multiSelectKeyPressed.value) {
    dropdownRef.value?.hide();
  }
}

function onChipClose(_: MouseEvent, option: Option) {
  remove(option);
}

function clear() {
  model.value = multiple ? [] : null;
  input.value = '';
}

function toggle() {
  if (readonly || innerDisabled.value) {
    return;
  }

  dropdownRef.value?.toggle();

  if (clearOnOpen) {
    model.value = multiple ? [] : null;
  }

  input.value = '';
}

function onFieldClick() {
  if (readonly || innerDisabled.value) {
    return;
  }

  if (!showInlineSearch.value) {
    toggle();
    return;
  }

  if (isOpen.value) {
    return;
  }

  if (clearOnOpen) {
    model.value = null;
  }

  input.value = '';
  dropdownRef.value?.show();
}

function onKeyDown(event: KeyboardEvent) {
  if (MULTI_SELECT_KEYS.includes(event.key)) {
    multiSelectKeyPressed.value = true;
  }
}

function onKeyUp(event: KeyboardEvent) {
  if (MULTI_SELECT_KEYS.includes(event.key)) {
    multiSelectKeyPressed.value = false;
  }
}

function onDropdownOpen() {
  emit('focus');
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);

  if (showInlineSearch.value) {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
}

function onFieldFocusOut(event: FocusEvent) {
  const next = event.relatedTarget;

  if (next instanceof Node && fieldRef.value?.contains(next)) {
    return;
  }

  isFocused.value = false;
  window.getSelection()?.removeAllRanges();
}

function clearSearchFocus() {
  isFocused.value = false;
  searchInputRef.value?.blur();
  window.getSelection()?.removeAllRanges();
}

function restoreFieldFocus() {
  clearSearchFocus();
  fieldRef.value?.focus();
  isFocused.value = true;
}

function shouldRestoreFieldFocus() {
  const active = document.activeElement;

  if (!active || active === document.body || active === document.documentElement) {
    return true;
  }

  if (active === searchInputRef.value || !!fieldRef.value?.contains(active)) {
    return true;
  }

  return active instanceof Element && !!active.closest('.ui-dropdown-list, .add-option');
}

function onDropdownClose() {
  if (withCustomOption && customOption.value && !model.value && !foundOptions.value.length) {
    select(customOption.value);
  }

  const keepFocus = shouldRestoreFieldFocus();

  input.value = '';

  if (keepFocus) {
    restoreFieldFocus();
  } else {
    clearSearchFocus();
  }

  emit('blur');
  document.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('keyup', onKeyUp);
  multiSelectKeyPressed.value = false;
}

onUnmounted(() => {
  document.removeEventListener('keydown', onKeyDown);
  document.removeEventListener('keyup', onKeyUp);
});

function onSearchKeydownTab() {
  dropdownRef.value?.hide();
  nextTick(restoreFieldFocus);
}

function open(search = '') {
  input.value = search;
  dropdownRef.value?.show();
}

function onEditListOption(option: Option) {
  dropdownRef.value?.hide();
  editOption?.(option);
}

function onAddListOption() {
  dropdownRef.value?.hide();
  addOption?.();
}

function onSelectCustomOption() {
  if (!customOption.value) {
    return;
  }

  select(customOption.value);
  dropdownRef.value?.hide();
  nextTick(restoreFieldFocus);
}

function onSearchKeydownEnter(event: KeyboardEvent) {
  if (!customOption.value || listRef.value?.getActive() != null) {
    return;
  }

  event.stopPropagation();
  onSelectCustomOption();
}

function getHighlightedParts(label: string) {
  const text = maybeT(label);
  const query = listQuery.value;

  if (!highlightMatches || !query) {
    return [{ text, match: false }];
  }

  const start = StringUtil.normalize(text).indexOf(query);

  if (start < 0) {
    return [{ text, match: false }];
  }

  const end = start + query.length;

  return [
    { text: text.slice(0, start), match: false },
    { text: text.slice(start, end), match: true },
    { text: text.slice(end), match: false },
  ].filter((part) => part.text);
}

defineExpose({
  choose,
  clear,
  open,
  updateOptions: () => getOptions(true),
});
</script>

<template>
  <UiDropdown
    ref="dropdown"
    v-model="isOpen"
    class="ui-combobox"
    :offset="offset"
    :placement="['bottom-start', 'top-start']"
    shrink
    anchor-width
    full-width
    no-overflow
    close-if-outside-click
    @open="onDropdownOpen"
    @close="onDropdownClose"
  >
    <template #anchor="{ isOpen: dropdownOpen }">
      <div
        ref="field"
        v-focus="focusOnMount"
        class="field"
        :class="[{ _shadow: dropdownOpen || isFocused }, className]"
        :tabindex="readonly || (showInlineSearch && dropdownOpen) ? -1 : 0"
        @click="onFieldClick"
        @keydown.enter.prevent="!readonly && !innerDisabled && onFieldClick()"
        @keydown.down.prevent="!readonly && !innerDisabled && onFieldClick()"
        @keydown.tab="!showInlineSearch && dropdownRef?.hide()"
        @focusin="isFocused = true"
        @focusout="onFieldFocusOut"
      >
        <div class="content">
          <slot name="prefix" />

          <UiChipList
            v-if="multiple && hasOptions"
            :options="model as Option[]"
            @close.stop="onChipClose"
          >
            <template #default="option">
              <slot v-bind="option" />
            </template>
          </UiChipList>

          <div
            v-else
            class="value-host"
            :class="{ '_inline-search': isInlineSearchOpen }"
          >
            <span
              v-if="!hasOptions"
              class="value _placeholder"
              :class="{ _invisible: isInlineSearchOpen }"
            >
              {{ $maybeT(placeholder) }}
            </span>

            <div
              v-else
              class="value"
              :class="{ _expanded: isValueExpanded, _invisible: isInlineSearchOpen }"
            >
              <slot v-bind="model">
                {{ $maybeT((model as Option).label) }}
              </slot>
            </div>

            <input
              v-if="isInlineSearchOpen"
              ref="searchInput"
              v-model="input"
              class="field-input"
              :placeholder="$maybeT(placeholder)"
              :style="searchMaxLength ? searchGradientStyle : undefined"
              @click.stop
              @keydown.tab="onSearchKeydownTab"
              @keydown.enter.prevent="onSearchKeydownEnter"
              @input="onSearchInput"
            />
          </div>

          <slot name="postfix" />

          <UiButtonIcon
            v-if="showClear"
            color="gray"
            :title="$t('ui.combobox.clickToClear')"
            tabindex="-1"
            @click.stop="clear"
          >
            <CloseIcon />
          </UiButtonIcon>

          <span
            v-if="showLock"
            class="lock"
          >
            <LockIcon class="lock-icon" />
          </span>
          <UiButtonIcon
            v-else-if="showChevron"
            v-rotate="dropdownOpen"
            color="gray"
            tabindex="-1"
            :error
            @click.stop="toggle"
          >
            <ChevronIcon />
          </UiButtonIcon>
        </div>
      </div>
    </template>

    <div class="body">
      <div
        v-if="isLoading"
        class="state"
      >
        <UiSpinner />
      </div>

      <template v-else>
        <div
          v-if="showDropdownSearch"
          class="search"
        >
          <input
            v-model="input"
            v-focus
            class="search-input"
            :placeholder="$t('ui.combobox.startTyping')"
            :style="searchGradientStyle"
            @keydown.tab="onSearchKeydownTab"
            @keydown.enter.prevent="onSearchKeydownEnter"
            @input="onSearchInput"
          />
          <SearchIcon class="search-icon" />
        </div>

        <div
          v-if="showEmptyState"
          class="state"
        >
          {{ $t('ui.combobox.nothingFound') }}
        </div>

        <template v-else>
          <UiDropdownList
            v-if="extendedOptions.length"
            ref="list"
            :options="extendedOptions"
            :checked-options="model"
            :is-option-editable="isOptionEditable"
            @select="onSelect"
            @edit="onEditListOption"
          >
            <template #default="option">
              <slot
                v-bind="option"
                :is-list="true"
              >
                <span class="option-label">
                  <template
                    v-for="(part, index) in getHighlightedParts(option.label)"
                    :key="`${option.code}-${index}`"
                  >
                    <b v-if="part.match">{{ part.text }}</b>
                    <template v-else>{{ part.text }}</template>
                  </template>
                </span>
              </slot>
            </template>
          </UiDropdownList>

          <template v-if="showCustomOptionPlate">
            <UiDropdownDivider
              v-if="extendedOptions.length"
              class="add-option-divider"
            />
            <button
              v-if="customOption"
              type="button"
              class="add-option"
              @mousedown.stop.prevent
              @click="onSelectCustomOption"
            >
              <PlusIcon class="add-option-icon" />
              <span>{{ customOption.label }}</span>
            </button>
            <div
              v-else
              class="add-option _hint"
            >
              <PlusIcon class="add-option-icon" />
              <span>{{ $t(typeToAddLabel) }}</span>
            </div>
          </template>

          <template v-if="showAddOption">
            <UiDropdownDivider
              v-if="extendedOptions.length || showCustomOptionPlate"
              class="add-option-divider"
            />
            <button
              type="button"
              class="add-option"
              @mousedown.stop.prevent
              @click="onAddListOption"
            >
              <PlusIcon class="add-option-icon" />
              <span>{{ $t(addOptionLabel!) }}</span>
            </button>
          </template>
        </template>
      </template>
    </div>
  </UiDropdown>
</template>

<style scoped lang="scss">
.ui-combobox {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.field {
  display: flex;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    box-shadow var(--transition-fast);

  &._size-m {
    min-height: 36px;
    padding: 0 8px 0 12px;
    font-size: var(--font-size-l);
    line-height: var(--font-size-l);

    .value,
    .field-input {
      padding: 11px 4px 11px 12px;
    }

    &._prefix {
      padding-left: 8px;

      .value,
      .field-input {
        padding-left: 4px;
      }
    }
  }

  &._size-l {
    min-height: 48px;
    padding: 0 12px 0 16px;
    font-size: var(--font-size-xl);
    line-height: var(--font-size-xl);

    .value,
    .field-input {
      padding: 16px 4px 16px 12px;
    }

    &._prefix {
      padding-left: 12px;

      .value,
      .field-input {
        padding-left: 4px;
      }
    }
  }

  &._color-gray {
    background-color: var(--color-gray-075);
  }

  &._color-dark-gray {
    background-color: var(--color-gray-100);
  }

  &._color-white {
    box-shadow: var(--shadow);
    background-color: var(--color-white);
  }

  &._prefix {
    .value-host {
      margin-left: 0;
    }

    .value,
    .field-input {
      margin-left: 0;
      padding-left: 16px;
    }

    .ui-chip-list {
      padding-left: 4px;
    }
  }

  &._disabled {
    pointer-events: none;

    .content {
      opacity: 0.5;
    }
  }

  &._readonly {
    cursor: default;
    background-color: var(--color-gray-050);
    box-shadow: none;
  }

  &._error {
    background-color: var(--color-red-100);

    &._open {
      box-shadow: var(--shadow-control-error);
    }

    .value,
    .field-input {
      color: var(--color-red-800);
    }
  }

  &._shadow {
    box-shadow: var(--shadow-control);
  }
}

.content {
  display: flex;
  flex-grow: 1;
  min-width: 0;
  align-items: center;

  > :deep(*:not(.value, .ui-chip-list, .field-input, .value-host)) {
    flex-shrink: 0;
  }

  > :deep(.ui-chip-list) {
    flex: 1 1 auto;
  }
}

.value-host {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: -12px;

  &._inline-search {
    .field-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      box-sizing: border-box;
    }
  }
}

.lock {
  display: flex;
  flex-shrink: 0;
  margin-left: 8px;
  align-items: center;
}

.lock-icon {
  width: 16px;
  height: 16px;
  fill: var(--color-gray-600);
}

.value {
  display: block;
  min-width: 0;
  transition: color var(--transition-fast);

  &:not(._expanded) {
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &._invisible {
    visibility: hidden;
  }

  &._placeholder {
    color: var(--color-gray-500);
  }

  &._expanded {
    overflow-wrap: anywhere;
    word-break: break-word;
  }
}

.field-input {
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  outline: none;
  cursor: text;

  &::placeholder {
    color: var(--color-gray-500);
  }
}

.ui-chip-list {
  flex: 1 1 auto;
  min-width: 0;
  margin-left: -8px;
  padding: 6px 4px 6px 0;
  max-height: 180px;
  overflow-y: auto;
}

.search {
  position: relative;
  background-color: var(--color-gray-075);
}

.search-input {
  width: 100%;
  height: 36px;
  padding-left: 12px;
  padding-right: 36px;

  &::placeholder {
    color: var(--color-gray-500);
  }
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
}

.state {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
}

:deep(.add-option-divider) {
  margin: 0;
}

.add-option {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  color: var(--color-gray-1000);
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  text-align: left;
  cursor: pointer;

  &._hint {
    color: var(--color-gray-500);
    cursor: default;
    pointer-events: none;

    .add-option-icon {
      fill: var(--color-gray-500);
    }
  }

  @media (hover: hover) {
    &:hover:not(._hint) {
      background-color: var(--color-gray-100);
    }
  }
}

.add-option-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  fill: var(--color-gray-1000);
}

.option-label {
  min-width: 0;
}

.body {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  box-shadow: var(--shadow);
  border-radius: 8px;
}
</style>
